import { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  InputAdornment,
} from "@mui/material";
import { Page, PageHeader } from "../../components/layout/Page";
import { useQuery } from "@tanstack/react-query";
import { fetchGenericCategories, fetchGenericItems } from "../../api/priceTables";
import { useAdminCrud } from "../../hooks/useAdminCrud";
import {
  ActionsHeadCell,
  ConfirmDeleteDialog,
  DataCard,
  DataToolbar,
  EditRowDialog,
  RefreshAndAdd,
  RowActions,
  SearchField,
  TableMessageRow,
  TablePager,
} from "./DataTableParts";
import { FULL_SPAN_SX, TIGHT_COL_SX, formatMoney } from "./dataTableStyles";
import { useRowDialogs } from "./useRowDialogs";
import { queryKeys } from "../../api/queryKeys";

interface GenericItem {
  id: number;
  category: string;
  brand: string | null;
  model: string | null;
  keywords: string[] | null;
  price: number;
  price_unit: string;
  typical_remarks: string | null;
  specs: Record<string, unknown> | null;
  notes: string | null;
  quote_count: number;
  confidence: string;
  last_quoted_price: number | null;
  last_quoted_at: string | null;
  created_at: string;
  updated_at: string;
}

/** What a create / update sends (price is null when the field is empty). */
type GenericItemPayload = Pick<
  GenericItem,
  "category" | "brand" | "model" | "keywords" | "price_unit" | "typical_remarks" | "notes" | "confidence"
> & { price: number | null };

interface GenericItemForm {
  category: string;
  brand: string;
  model: string;
  keywords: string;
  price: string;
  price_unit: string;
  typical_remarks: string;
  notes: string;
  confidence: string;
}

const emptyForm: GenericItemForm = {
  category: "",
  brand: "",
  model: "",
  keywords: "",
  price: "",
  price_unit: "each",
  typical_remarks: "",
  notes: "",
  confidence: "medium",
};

const PRICE_UNITS = ["each", "per_meter", "per_set", "per_pack", "per_box"];
const CONFIDENCE_LEVELS = ["high", "medium", "low"];
// Category, Brand / Model, Price, Keywords, Confidence, Quote Count, Updated, Actions
const COL_COUNT = 8;

const toForm = (item: GenericItem): GenericItemForm => ({
  category: item.category,
  brand: item.brand || "",
  model: item.model || "",
  keywords: item.keywords?.join(", ") || "",
  price: item.price?.toString() || "",
  price_unit: item.price_unit || "each",
  typical_remarks: item.typical_remarks || "",
  notes: item.notes || "",
  confidence: item.confidence || "medium",
});

const formatCategory = (category: string) => {
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getConfidenceColor = (confidence: string): "success" | "warning" | "error" | "default" => {
  switch (confidence) {
    case "high":
      return "success";
    case "medium":
      return "warning";
    case "low":
      return "error";
    default:
      return "default";
  }
};

/** One generic item: category, brand / model, price, keywords, confidence, use, date. */
function GenericItemRow({ item, onEdit, onDelete }: { item: GenericItem; onEdit: () => void; onDelete: () => void }) {
  return (
    <TableRow hover>
      <TableCell sx={TIGHT_COL_SX}>
        <Chip
          label={formatCategory(item.category)}
          size="small"
          variant="outlined"
          color="primary"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {item.brand || "-"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {item.model || "-"}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={TIGHT_COL_SX}>
        <Typography variant="body2" fontWeight={600}>
          {formatMoney(item.price)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {item.price_unit}
        </Typography>
      </TableCell>
      <TableCell>
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {item.keywords?.slice(0, 3).map((kw, idx) => (
            <Chip key={idx} label={kw} size="small" />
          ))}
          {item.keywords && item.keywords.length > 3 && (
            <Chip label={`+${item.keywords.length - 3}`} size="small" variant="outlined" />
          )}
        </Box>
      </TableCell>
      <TableCell sx={TIGHT_COL_SX}>
        <Chip
          label={item.confidence}
          size="small"
          color={getConfidenceColor(item.confidence)}
        />
      </TableCell>
      <TableCell align="right" sx={TIGHT_COL_SX}>
        {item.quote_count}
      </TableCell>
      <TableCell sx={{ ...TIGHT_COL_SX, color: "text.secondary" }}>
        {new Date(item.updated_at).toLocaleDateString()}
      </TableCell>
      <RowActions
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </TableRow>
  );
}

export default function GenericDatabaseManagement() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const { editDialog, deleteDialog, formData, setFormData, isAddMode, openEdit: handleOpenEditDialog, closeEdit: handleCloseDialog, openDelete, closeDelete } =
    useRowDialogs<GenericItem, GenericItemForm>(emptyForm, toForm);

  // Fetch generic items
  const {
    data: genericItemsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.genericItems.list(page, rowsPerPage, searchQuery, categoryFilter),
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (searchQuery) params.append("search", searchQuery);
      if (categoryFilter) params.append("category", categoryFilter);
      
      return fetchGenericItems(params);
    },
    staleTime: 30000,
  });

  // Fetch categories for filter
  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.genericCategories,
    queryFn: fetchGenericCategories,
    staleTime: 60000,
  });

  const { createMutation, updateMutation, deleteMutation } = useAdminCrud<GenericItemPayload>({
    endpoint: "/generic-items",
    label: "generic item",
    invalidate: queryKeys.genericItems.all,
    createAlsoInvalidates: [queryKeys.genericCategories],
    onSaved: () => handleCloseDialog(),
    onDeleted: () => closeDelete(),
  });

  const handleSave = () => {
    const data: GenericItemPayload = {
      category: formData.category,
      brand: formData.brand || null,
      model: formData.model || null,
      keywords: formData.keywords ? formData.keywords.split(",").map((k) => k.trim()).filter((k) => k) : null,
      price: formData.price && String(formData.price).trim() !== "" ? parseFloat(formData.price) : null,
      price_unit: formData.price_unit,
      typical_remarks: formData.typical_remarks || null,
      notes: formData.notes || null,
      confidence: formData.confidence,
    };

    if (isAddMode) {
      createMutation.mutate(data);
    } else if (editDialog.item) {
      updateMutation.mutate({ id: editDialog.item.id, data });
    }
  };

  const handleDelete = () => {
    if (deleteDialog.item) {
      deleteMutation.mutate(deleteDialog.item.id);
    }
  };

  const genericItems = genericItemsData?.data || [];
  const totalCount = genericItemsData?.pagination?.total || 0;

  return (
    <Page width="full">
      <PageHeader
        title="Generic Items Database"
        subtitle="Manage learned generic item pricing from historical quotes"
        actions={<RefreshAndAdd onRefresh={() => refetch()} onAdd={() => handleOpenEditDialog(null)} />}
      />
      <DataCard>
        <DataToolbar summary={genericItemsData ? `${totalCount.toLocaleString()} ${totalCount === 1 ? "item" : "items"}` : undefined}>
          <SearchField placeholder="Search..." value={searchQuery} onChange={setSearchQuery} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat: string) => (
                <MenuItem key={cat} value={cat}>
                  {formatCategory(cat)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DataToolbar>

        {error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load generic items: {(error as Error).message}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={TIGHT_COL_SX}>Category</TableCell>
                    <TableCell>Brand / Model</TableCell>
                    <TableCell align="right" sx={TIGHT_COL_SX}>Price</TableCell>
                    <TableCell>Keywords</TableCell>
                    <TableCell sx={TIGHT_COL_SX}>Confidence</TableCell>
                    <TableCell align="right" sx={TIGHT_COL_SX}>Quote Count</TableCell>
                    <TableCell sx={TIGHT_COL_SX}>Updated</TableCell>
                    <ActionsHeadCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableMessageRow colSpan={COL_COUNT} loading />
                  ) : genericItems.length === 0 ? (
                    <TableMessageRow colSpan={COL_COUNT}>
                      No generic items found. Click "Add New" to add your first entry.
                    </TableMessageRow>
                  ) : (
                    genericItems.map((item: GenericItem) => (
                      <GenericItemRow
                        key={item.id}
                        item={item}
                        onEdit={() => handleOpenEditDialog(item)}
                        onDelete={() => openDelete(item)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePager count={totalCount} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
          </>
        )}
      </DataCard>

      {/* Edit/Add Dialog */}
      <EditRowDialog
        open={editDialog.open}
        title={isAddMode ? "Add New Generic Item" : "Edit Generic Item"}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveDisabled={!formData.category || !formData.price || createMutation.isPending || updateMutation.isPending}
        saving={createMutation.isPending || updateMutation.isPending}
      >
        <TextField
          label="Category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          fullWidth
          required
          size="small"
          helperText="Use snake_case (e.g., usb_blocker, webcam, ups)"
          sx={FULL_SPAN_SX}
        />
        <TextField
          label="Brand"
          value={formData.brand}
          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
          fullWidth
          size="small"
        />
        <TextField
          label="Model"
          value={formData.model}
          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
          fullWidth
          size="small"
        />
        <TextField
          label="Price"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          type="number"
          required
          fullWidth
          size="small"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <FormControl fullWidth size="small">
          <InputLabel>Price Unit</InputLabel>
          <Select
            value={formData.price_unit}
            label="Price Unit"
            onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
          >
            {PRICE_UNITS.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit.replace("_", " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Keywords"
          value={formData.keywords}
          onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
          fullWidth
          size="small"
          helperText="Comma-separated keywords for matching"
        />
        <FormControl fullWidth size="small">
          <InputLabel>Confidence</InputLabel>
          <Select
            value={formData.confidence}
            label="Confidence"
            onChange={(e) => setFormData({ ...formData, confidence: e.target.value })}
          >
            {CONFIDENCE_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Typical Remarks"
          value={formData.typical_remarks}
          onChange={(e) => setFormData({ ...formData, typical_remarks: e.target.value })}
          fullWidth
          multiline
          rows={2}
          size="small"
          sx={FULL_SPAN_SX}
        />
        <TextField
          label="Notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          fullWidth
          multiline
          rows={2}
          size="small"
          sx={FULL_SPAN_SX}
        />
      </EditRowDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog open={deleteDialog.open} onCancel={closeDelete} onConfirm={handleDelete} deleting={deleteMutation.isPending}>
        Are you sure you want to delete the generic item{" "}
        <strong>
          {deleteDialog.item?.category}
          {deleteDialog.item?.brand && ` - ${deleteDialog.item.brand}`}
          {deleteDialog.item?.model && ` ${deleteDialog.item.model}`}
        </strong>
        ? This action cannot be undone.
      </ConfirmDeleteDialog>
    </Page>
  );
}
